'use client';

import { useEffect, useMemo, useState } from 'react';
import type { Zone } from '@/types/zones/zone';
import { MESSAGES, type Message } from '@/utils/message-broker/types';
import { MessageBroker } from '@/utils/message-broker';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { ROUTES } from '@/utils/constants';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import type { Region } from '@/types/constants';
import { getRecentlyPlayedZone, parseRecentlyPlayedZone } from '@/logic/games/recently-played-games';
import type { ParsedGameZone } from '@/logic/games/parse-game-zone';
import type { Game } from '@/types/games';

let cachedRecentlyPlayedZone: Zone | null = null;

type UseRecentlyPlayedZoneOptions = {
    initialZone: Zone;
    gridLayout?: PRISMIC_ZONE_LAYOUT;
};

export const useRecentlyPlayedZone = ({
    initialZone,
    gridLayout = PRISMIC_ZONE_LAYOUT.DEFAULT
}: UseRecentlyPlayedZoneOptions): ParsedGameZone | null => {
    const { region, isMobile, featureFlags, currentRoute } = useGlobalContext();
    const setRecentGames = useStore((state) => state.userContext.setRecentGames);
    const [zone, setZone] = useState<Zone>(() => cachedRecentlyPlayedZone ?? initialZone);

    const animatedTileEnabled = currentRoute === ROUTES.HOMEPAGE && featureFlags[PRODUCT_THROTTLES.HAS_ANIMATED_TILES];

    const zoneData = useMemo(
        () =>
            parseRecentlyPlayedZone(zone, gridLayout, { region: region as Region, isMobile }, { animatedTileEnabled }),
        [zone, gridLayout, region, isMobile, animatedTileEnabled]
    );

    useEffect(() => {
        const onRefresh = async (msg: Message) => {
            if (msg.data.type !== MESSAGES.REFRESH_RECENTLY_PLAYED) {
                return;
            }

            const recentlyPlayedZone = await getRecentlyPlayedZone();

            if (recentlyPlayedZone) {
                cachedRecentlyPlayedZone = recentlyPlayedZone;
                setZone(recentlyPlayedZone);
            }
        };

        MessageBroker.subscribe(onRefresh);

        return () => MessageBroker.unsubscribe(onRefresh);
    }, []);

    useEffect(() => {
        const items = (zone.data.body[0]?.items ?? []) as Game[];
        setRecentGames(items.map((game) => game.uid));
    }, [zone, setRecentGames]);

    if (!zoneData.games.length) {
        return null;
    }

    return zoneData;
};
